/**
 * Safe SVG renderer.
 *
 * Why this exists:
 *   - Some lesson content (e.g. `mouth_svg` for Sound Foundations) is
 *     authored as inline SVG on the backend and rendered via
 *     `dangerouslySetInnerHTML`. While the SVG strings are server-
 *     controlled today (seeded from `foundations_pack.py`), there is no
 *     guarantee a future migration won't open them up to user input.
 *   - DOMPurify with the SVG profile gives us defense-in-depth: even
 *     if a malicious SVG ever lands in the DB, embedded `<script>`,
 *     `on*` handlers, and `javascript:` URLs are stripped before render.
 *
 * Use as:
 *   import { renderSafeSvg } from '@/lib/safeSvg';
 *   <div {...renderSafeSvg(item.mouth_svg)} />
 */
import DOMPurify from 'dompurify';

// Profile: USE_PROFILES.svg only allows safe SVG/MathML primitives
// (no scripts, no foreignObject XSS surface, no `on*` attrs).
const PURIFY_OPTS = {
  USE_PROFILES: { svg: true, svgFilters: true },
};

/**
 * Returns a `dangerouslySetInnerHTML`-shaped object with sanitized SVG.
 *
 * Returns an empty `__html` string if the input is null/empty/non-string,
 * so callers can safely spread it without conditional branches.
 *
 * @param {string} svg  Raw SVG markup
 * @returns {{ dangerouslySetInnerHTML: { __html: string } }}
 */
export function renderSafeSvg(svg) {
  const safe = typeof svg === 'string' && svg ? DOMPurify.sanitize(svg, PURIFY_OPTS) : '';
  return { dangerouslySetInnerHTML: { __html: safe } };
}
